/**
 * @file src/core/constants/collections.ts
 * @description Centraliza los nombres de las colecciones y subcolecciones de Firestore
 * para evitar errores de tipeo y facilitar el mantenimiento.
 */

export const COLECCIONES = {
  /** Colección principal de usuarios */
  USUARIOS: 'usuarios',

  /**
   * Subcolección dentro de un usuario que contiene los datos de la aplicación.
   * Se almacena aquí para optimizar lecturas y mantener los datos del usuario separados.
   */
  APP_DATA: 'appData',

  /**
   * Subcolección dentro de un usuario para gestionar su lista de clientes.
   * Separada de appData para permitir consultas más específicas sobre clientes sin
   * tener que leer el documento 'mainData' completo.
   */
  CLIENTES: 'clientes',

  /**
   * Subcolección dentro de un cliente para registrar su historial de actividades.
   * (Ej: creación, cambio de estado de pedido, notas, etc.).
   */
  ACTIVIDADES_CLIENTE: 'actividades',

  /**
   * Colección global para todas las notificaciones del sistema.
   * Cada notificación tiene un userId para filtrar por usuario.
   */
  NOTIFICATIONS: 'notifications',

  /**
   * Colección global para tokens FCM de los dispositivos.
   * Utilizada para envío de notificaciones push.
   */
  FCM_TOKENS: 'fcmTokens',

  /**
   * Colección de transacciones de egresos (gastos) del negocio.
   * Cada documento es un gasto registrado con su ownerId.
   */
  EGRESOS_NEGOCIO: 'egresosNegocio',

  /**
   * Colección para configuraciones de gastos recurrentes.
   * Almacena las reglas de pagos recurrentes, no las transacciones.
   */
  CONFIG_RECURRENTES: 'config_recurrentes',

  /**
   * Colección de categorías de gastos personalizadas: un solo documento por
   * usuario (id = ownerId). Se lee una vez y se cachea para no gastar lecturas.
   */
  CATEGORIAS_GASTOS: 'categorias_gastos',

  /**
   * Colección de "Otros Ingresos" del negocio (entradas de dinero que no son ventas:
   * préstamos, aportes de capital, venta de activos, alquileres, reembolsos, etc.).
   * Cada documento es un ingreso registrado con su ownerId.
   */
  OTROS_INGRESOS: 'otrosIngresos',

  /**
   * Colección de transacciones de gastos personales (fuera del negocio).
   * Cada documento es un gasto registrado con su ownerId.
   */
  GASTOS_PERSONALES: 'gastosPersonales',

  /**
   * Configuración de gastos personales: un solo documento por usuario (id = ownerId).
   * Guarda el presupuesto mensual y la moneda preferida. Se lee una vez y se cachea.
   */
  GASTOS_PERSONALES_CONFIG: 'gastos_personales_config',

  /**
   * Colección para el mapeo de respuestas inteligentes (Keywords -> Media URLs).
   * Facilita la consulta rápida de URLs para el BotEngine.
   */
  CONFIG_RESPUESTAS: 'config_respuestas',

  /**
   * Subcolección dentro de un usuario para sesiones del Canvas Marketing IA.
   * Ruta: usuarios/{userId}/marketingSessions/{sessionId}
   */
  MARKETING_SESSIONS: 'marketingSessions',

  /**
   * Subcolección dentro de una sesión de marketing para mensajes del chat IA.
   * Ruta: usuarios/{userId}/marketingSessions/{sessionId}/messages/{messageId}
   */
  MARKETING_MESSAGES: 'messages',

  /**
   * Subcolección dentro de una sesión de marketing para imágenes generadas por IA.
   * Ruta: usuarios/{userId}/marketingSessions/{sessionId}/generatedImages/{imageId}
   */
  MARKETING_IMAGES: 'generatedImages',

  /**
   * Colección de Tiendas Web (PLAN-TIENDA-WEB.md). Cada documento es una tienda
   * con su ownerId, slug único y configuración de dominio/WhatsApp/SEO.
   */
  TIENDAS: 'tiendas',

  /**
   * Reserva de slugs únicos de tiendas: el ID del documento ES el slug.
   * Garantiza unicidad global con 1 sola lectura (transacción al crear/renombrar).
   */
  TIENDAS_SLUGS: 'tiendas_slugs',

  /**
   * Subcolección de páginas de una tienda: tiendas/{tiendaId}/paginas/{paginaId}.
   * Cada página guarda su diseño completo (bloques) en un solo documento.
   */
  TIENDA_PAGINAS: 'paginas',

  /**
   * Métricas agregadas de tiendas (visitas/pedidos). Escritas SOLO por Cloud
   * Functions con FieldValue.increment; un documento por tienda y mes.
   */
  METRICAS_TIENDA: 'metricasTienda'
} as const;

/**
 * ID del documento principal dentro de la subcolección APP_DATA.
 * Contiene arrays grandes como ventasData, egresosData, etc.
 * Esta estrategia (documento único con arrays) es útil para datos que se leen juntos,
 * pero debe manejarse con cuidado para no exceder el límite de 1MB por documento.
 */
export const MAIN_DATA_DOC_ID = 'mainData';
export const USUARIOS_COLLECTION_NAME = 'usuarios';
export const CLIENTES_COLLECTION_NAME = 'clientes';
export const ACTIVIDADES_CLIENTE_SUBCOLLECTION_NAME = 'actividades';
export const NOTIFICACIONES_SUBCOLLECTION_NAME = 'notificaciones';
export const APP_DATA_COLLECTION_NAME = 'appData';
export const OTROS_INGRESOS_COLLECTION_NAME = 'otrosIngresos';
export const MARKETING_SESSIONS_COLLECTION_NAME = 'marketingSessions';
export const MARKETING_MESSAGES_COLLECTION_NAME = 'messages';
export const MARKETING_IMAGES_COLLECTION_NAME = 'generatedImages';
